import type {
  Claim,
  ProblemRecord,
  Program,
  Recommendation,
  RecommendationUrgency,
  Supplier,
  WorkflowStage,
} from "./types";
import { recordDelayCost, recordNpr, recordVerdict, rpnBefore } from "./calc";
import { DEFAULT_EIGHTD_MAPPING, missingStageEvidence, type EightDMapping } from "./stage-link";
import { apBefore } from "./action-priority";

const fmtMoney = (n: number) =>
  n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(1)}M` : `$${Math.round(n / 1000)}k`;

interface ClaimStats {
  count: number;
  avgCost: number;
  topRegion: string;
  topRegionShare: number;
  topDealer: string;
  topDealerCount: number;
  topCode: string;
  topCodeShare: number;
  medianMileage: number;
  latest: string;
}

export function claimStats(claims: Claim[]): ClaimStats {
  if (claims.length === 0) {
    return {
      count: 0,
      avgCost: 0,
      topRegion: "—",
      topRegionShare: 0,
      topDealer: "—",
      topDealerCount: 0,
      topCode: "—",
      topCodeShare: 0,
      medianMileage: 0,
      latest: "—",
    };
  }
  const tally = (key: (c: Claim) => string) => {
    const map = new Map<string, number>();
    for (const c of claims) map.set(key(c), (map.get(key(c)) ?? 0) + 1);
    return [...map.entries()].sort((a, b) => b[1] - a[1])[0] ?? ["—", 0];
  };
  const [topRegion, regionCount] = tally((c) => c.region) as [string, number];
  const [topDealer, dealerCount] = tally((c) => c.dealer) as [string, number];
  const [topCode, codeCount] = tally((c) => c.failureCode) as [string, number];
  const mileages = claims.map((c) => c.mileage).sort((a, b) => a - b);
  const total = claims.reduce((s, c) => s + c.totalCost, 0);
  const latest =
    [...claims].sort((a, b) => (a.repairDate < b.repairDate ? 1 : -1))[0]?.repairDate ?? "—";
  return {
    count: claims.length,
    avgCost: Math.round(total / claims.length),
    topRegion,
    topRegionShare: Math.round((regionCount / claims.length) * 100),
    topDealer,
    topDealerCount: dealerCount,
    topCode,
    topCodeShare: Math.round((codeCount / claims.length) * 100),
    medianMileage: mileages[Math.floor(mileages.length / 2)] ?? 0,
    latest,
  };
}

function clamp(n: number) {
  return Math.max(46, Math.min(96, Math.round(n)));
}

/**
 * Deterministic recommendation engine. Every recommendation is derived from data
 * already inside the governed record, the claim feed, the supplier record and the
 * program, so the demo needs no model call to show a decision point.
 */
export function stageRecommendations(
  record: ProblemRecord,
  stage: WorkflowStage,
  claims: Claim[],
  supplier?: Supplier | undefined,
  program?: Program | undefined,
  mapping: EightDMapping = DEFAULT_EIGHTD_MAPPING,
): Recommendation[] {
  const s = claimStats(claims);
  const npr = recordNpr(record);
  const delay = recordDelayCost(record);
  const worst = [...record.pfmea].sort((a, b) => rpnBefore(b) - rpnBefore(a))[0];
  const openContainment = record.containment.filter((c) => c.status !== "verified");
  const openRecovery = record.recovery.filter((r) => r.status !== "recovered");
  const openReadAcross = record.readAcross.filter((r) => r.status !== "cleared");
  const missing = missingStageEvidence(record, stage, mapping);
  const supplierName = supplier?.name ?? record.supplier;
  const out: Recommendation[] = [];

  const push = (
    key: string,
    title: string,
    action: string,
    rationale: string,
    basis: string[],
    confidence: number,
    urgency: RecommendationUrgency,
    impact?: string,
  ) =>
    out.push({
      id: `${record.id}:${stage.id}:${key}`,
      stageId: stage.id,
      stageIndex: stage.index,
      title,
      action,
      rationale,
      basis,
      confidence: clamp(confidence),
      urgency,
      impact,
      clause: stage.clause.split(" — ")[0] ?? stage.clause,
    });

  switch (stage.index) {
    case 1:
      push(
        "promote",
        "Promote the signal to a governed record",
        `Open a governed Problem Record for ${record.component} on ${program?.name ?? record.platform} and assign ${record.owner} as owner.`,
        `${s.count} linked claims with ${s.topCodeShare}% carrying failure code ${s.topCode}, and C/1000 is running above the ${program?.c1000Target ?? 0} target. The pattern is repeatable, not noise.`,
        [
          `${s.count} claims`,
          `code ${s.topCode} ${s.topCodeShare}%`,
          `avg claim ${fmtMoney(s.avgCost)}`,
          `latest repair ${s.latest}`,
        ],
        68 + record.riskScore / 4,
        record.riskScore >= 70 ? "now" : "this-week",
        `Exposure at stake ${fmtMoney(record.financial.exposure)}`,
      );
      break;
    case 2:
      push(
        "top5",
        "Hold this record in the management Top 5",
        `Keep ${record.code} in the Top 5 review with a weekly cadence and ${record.champion} as champion.`,
        `Remaining exposure is ${fmtMoney(npr)} and every month of delay carries about ${fmtMoney(delay)}. That ranks it above lower-exposure items competing for the same review slot.`,
        [
          `Remaining exposure ${fmtMoney(npr)}`,
          `delay cost ${fmtMoney(delay)}/mo`,
          `risk score ${record.riskScore}`,
        ],
        74 + record.financial.exposure / 400_000,
        "this-week",
        `Delay cost avoided ${fmtMoney(delay)} per month`,
      );
      break;
    case 3:
      push(
        "evidence",
        "Pull the evidence package that closes this gate",
        missing.length > 0
          ? `Request ${missing.slice(0, 2).join(" and ")} from ${supplierName} and the plant, aggregate level only.`
          : `Evidence set is complete — route it to ${record.champion} for governance review.`,
        `${s.topRegionShare}% of claims sit in ${s.topRegion} and ${s.topDealerCount} come from ${s.topDealer}. Regional and dealer concentration points the evidence request at build source rather than usage.`,
        [
          `${s.topRegion} ${s.topRegionShare}%`,
          `${s.topDealer} ${s.topDealerCount} claims`,
          `median ${s.medianMileage.toLocaleString()} mi`,
        ],
        missing.length > 0 ? 71 : 88,
        missing.length > 0 ? "now" : "scheduled",
      );
      break;
    case 4:
      push(
        "govern",
        "Lock the record definition and closure target",
        `Confirm the problem statement on ${record.failureCause}, keep ${record.owner} as owner and hold the ${record.targetCloseAt.slice(0, 10)} closure target.`,
        `The failure description, component and cause hypothesis are consistent across the claim set, so the record can be governed without splitting it.`,
        [
          `cause: ${record.failureCause}`,
          `plant ${record.plant}`,
          `${record.unitsInService.toLocaleString()} units in service`,
        ],
        82,
        "scheduled",
      );
      break;
    case 5:
      push(
        "contain",
        "Contain by build source, not by whole fleet",
        openContainment.length > 0
          ? `Extend "${openContainment[0]?.action}" to ${s.topRegion} and add 100% inspection at ${record.plant} until the root cause is proven.`
          : `Add 100% inspection at ${record.plant} for ${record.component} until the root cause is proven.`,
        `Containment scoped to ${record.plant} and ${s.topRegion} covers the concentration in the claim feed while avoiding fleet-wide cost that the data does not support.`,
        [
          `${openContainment.length} open containment action(s)`,
          `${s.topRegion} ${s.topRegionShare}%`,
          `plant ${record.plant}`,
        ],
        76 + s.topRegionShare / 6,
        "now",
        `Avoided fleet-wide containment cost`,
      );
      if (supplier)
        push(
          "supplier-8d",
          "Request a supplier 8D with a dated response",
          `Issue a formal 8D request to ${supplier.contactName} (${supplier.contactTitle}, ${supplier.name}) under ${supplier.contractRef}, response due in 10 working days.`,
          `Supplier quality sits at ${supplier.qualityRating}/5 with ${supplier.ppm} PPM, so a documented 8D is warranted rather than an informal query.`,
          [
            `${supplier.name} ${supplier.ppm} PPM`,
            `rating ${supplier.qualityRating}`,
            `SQE ${supplier.sqeOwner}`,
          ],
          79,
          "now",
        );
      break;
    case 6:
      push(
        "root-cause",
        "Focus root cause on the highest-RPN path",
        worst
          ? `Test "${worst.cause}" on ${worst.process} first, with ${worst.failureMode} as the confirmed failure mode.`
          : `Run a structured cause investigation on ${record.component} before committing corrective action.`,
        worst
          ? `Action Priority ${apBefore(worst)} (RPN legacy ${rpnBefore(worst)}) on this path; the RPN is the highest in the PFMEA and matches the field failure mode, so it is the cheapest hypothesis to falsify first.`
          : `No PFMEA path is yet rated, so cause work has to precede any corrective commitment.`,
        worst
          ? [
              `AP ${apBefore(worst)}`,
              `RPN (legacy) ${rpnBefore(worst)}`,
              worst.process,
              `code ${s.topCode}`,
            ]
          : [`${s.count} claims`],
        80,
        "this-week",
      );
      break;
    case 7:
      push(
        "change",
        "Take the process control route before a design change",
        worst
          ? `Implement "${worst.controlTechnique}" at ${record.plant} and re-validate, holding any design change until effectiveness is measured.`
          : `Define the engineering or process change with ${record.champion} before release.`,
        `A process control change is reversible and can be verified inside one build cycle; a design change on ${record.component} would extend the breakpoint and the carry cost of ${fmtMoney(delay)} per month.`,
        [worst?.controlMethod ?? "control method", `carry ${fmtMoney(delay)}/mo`, record.plant],
        77,
        "this-week",
        `Faster breakpoint, ${fmtMoney(delay)} per month of carry avoided`,
      );
      break;
    case 8:
      push(
        "breakpoint",
        "Set a single traceable breakpoint",
        `Declare one build breakpoint at ${record.plant} for ${record.component}, publish it to service and dealers, and hold pre-breakpoint stock for rework.`,
        `A single breakpoint keeps read-across and effectiveness measurable; staggered introductions would make the post-change claim curve unreadable.`,
        [`plant ${record.plant}`, `${s.count} claims baseline`, `latest repair ${s.latest}`],
        84,
        "this-week",
      );
      if (openRecovery.length > 0)
        push(
          "recovery",
          "Open supplier cost recovery now",
          `Submit a recovery claim of ${fmtMoney(record.financial.supplierRecoveryTarget - record.financial.supplierRecovered)} to ${supplierName} while the evidence is fresh.`,
          `${fmtMoney(record.financial.supplierRecovered)} of a ${fmtMoney(record.financial.supplierRecoveryTarget)} target has been recovered. Recovery strength falls once the breakpoint closes the evidence window.`,
          [
            `${openRecovery.length} open recovery action(s)`,
            `target ${fmtMoney(record.financial.supplierRecoveryTarget)}`,
          ],
          73,
          "now",
          `Recoverable ${fmtMoney(record.financial.supplierRecoveryTarget - record.financial.supplierRecovered)}`,
        );
      break;
    case 9:
      push(
        "pfmea",
        "Update PFMEA occurrence and detection, not severity",
        worst
          ? `Reduce occurrence to ${Math.max(worst.occAfter, 2)} and detection to ${Math.max(worst.detAfter, 2)} on ${worst.process} once the new control is proven; leave severity at ${worst.sevBefore}.`
          : `Add the confirmed failure mode to the PFMEA and update the control plan reaction plan.`,
        `Severity reflects customer effect and does not change with a control. Lowering it would overstate the improvement and would not survive an IATF audit.`,
        worst
          ? [
              `sev ${worst.sevBefore} unchanged`,
              `AP ${apBefore(worst)} baseline`,
              `RPN (legacy) ${rpnBefore(worst)}`,
              worst.frequency,
            ]
          : ["PFMEA update"],
        86,
        "scheduled",
      );
      break;
    case 10: {
      const effective = recordVerdict(record.financial) === "Effective";
      push(
        "effectiveness",
        effective ? "Declare the action effective" : "Hold the effectiveness verdict open",
        effective
          ? `Record the corrective action as effective — observed reduction ${record.financial.observedReductionPct}% against a ${record.financial.targetReductionPct}% target — and move to prevention.`
          : `Do not declare effectiveness yet: observed reduction is ${record.financial.observedReductionPct}% against a ${record.financial.targetReductionPct}% target. Extend monitoring by one more build month.`,
        `Post-breakpoint claim rate is the only accepted proof. ${s.count} claims form the baseline and the latest repair is dated ${s.latest}.`,
        [
          `observed ${record.financial.observedReductionPct}%`,
          `target ${record.financial.targetReductionPct}%`,
          `${s.count} claims baseline`,
        ],
        effective ? 88 : 70,
        effective ? "scheduled" : "this-week",
      );
      break;
    }
    case 11:
      push(
        "read-across",
        openReadAcross.length > 0 ? "Clear read-across before closing" : "Review final closure",
        openReadAcross.length > 0
          ? `Apply the verified learning to ${openReadAcross
              .map((r) => r.program)
              .slice(0, 2)
              .join(
                " and ",
              )} before closure — ${fmtMoney(openReadAcross.reduce((sum, r) => sum + r.exposure, 0))} of related exposure is still open.`
          : `Review ${record.code} for closure using attached workflow evidence and the recorded containment and effectiveness results.`,
        openReadAcross.length > 0
          ? `Closing while shared-component programs remain unreviewed is how the same failure returns as a new signal.`
          : `No read-across item is outstanding. Workflow completion does not establish measured effectiveness; a separate authorized reviewer must assess the evidence before closure.`,
        openReadAcross.length > 0
          ? [
              `${openReadAcross.length} program(s) open`,
              `${fmtMoney(openReadAcross.reduce((sum, r) => sum + r.exposure, 0))} related exposure`,
            ]
          : ["no open read-across items", "independent closure review required"],
        openReadAcross.length > 0 ? 81 : 90,
        openReadAcross.length > 0 ? "this-week" : "scheduled",
      );
      break;
    default:
      break;
  }

  if (missing.length > 0 && stage.status !== "complete" && stage.index !== 3)
    push(
      "gate",
      "Close the evidence gate before completing this stage",
      `Attach ${missing.slice(0, 2).join(" and ")}${missing.length > 2 ? ` and ${missing.length - 2} more` : ""} to release the gate on stage ${stage.index}.`,
      `The gate is blocking completion with ${missing.length} required item(s) outstanding, which is also the audit finding an assessor would raise first.`,
      missing.slice(0, 3),
      75,
      "now",
    );

  return out;
}

export function recordRecommendations(
  record: ProblemRecord,
  claims: Claim[],
  supplier?: Supplier | undefined,
  program?: Program | undefined,
  mapping: EightDMapping = DEFAULT_EIGHTD_MAPPING,
): Recommendation[] {
  return record.stages.flatMap((stage) =>
    stageRecommendations(record, stage, claims, supplier, program, mapping),
  );
}

export function openRecommendations(
  record: ProblemRecord,
  claims: Claim[],
  supplier?: Supplier | undefined,
  program?: Program | undefined,
  mapping: EightDMapping = DEFAULT_EIGHTD_MAPPING,
): Recommendation[] {
  const decided = new Set((record.decisions ?? []).map((d) => d.recommendationId));
  return recordRecommendations(record, claims, supplier, program, mapping)
    .filter((r) => !decided.has(r.id))
    .filter((r) => {
      const stage = record.stages.find((s) => s.id === r.stageId);
      return stage?.status !== "complete";
    });
}
